import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { AD_CONFIG } from '../config/ads';

interface GoogleAdProps {
    /**
     * Ad unit ID from Google AdSense (e.g., '1234567890')
     * Get this from AdSense dashboard → Ads → By ad unit
     */
    adSlot: string;
    /**
     * Ad format: 'auto', 'rectangle', 'horizontal', 'vertical'
     */
    format?: 'auto' | 'rectangle' | 'horizontal' | 'vertical';
    /**
     * Ad size: 'responsive' or specific dimensions
     */
    style?: React.CSSProperties;
    /**
     * Custom className
     */
    className?: string;
    /**
     * Whether to show a placeholder when ad is loading
     */
    showPlaceholder?: boolean;
}

/**
 * Google AdSense Ad Component
 * 
 * Usage:
 * <GoogleAd adSlot="ca-pub-XXXXXXXXXXXXXXXX/1234567890" />
 * 
 * Make sure to:
 * 1. Add your AdSense publisher ID to index.html
 * 2. Get ad unit IDs from Google AdSense dashboard
 * 3. Wait for AdSense approval before ads show
 */
const GoogleAd: React.FC<GoogleAdProps> = ({
    adSlot,
    format = 'auto',
    style,
    className,
    showPlaceholder = true,
}) => {
    const adRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = React.useState(false);
    const shouldRenderAd = AD_CONFIG.adsEnabled && Boolean(adSlot);

    useEffect(() => {
        if (!shouldRenderAd) {
            return;
        }

        // Check if adsbygoogle is available (script loaded)
        if (typeof window !== 'undefined' && (window as any).adsbygoogle) {
            try {
                // Push ad to adsbygoogle array
                ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
                setIsLoaded(true);
            } catch (err) {
                console.error('Error loading Google Ad:', err);
            }
        } else {
            // If script not loaded, try to load it
            const script = document.createElement('script');
            script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CONFIG.publisherId}`;
            script.async = true;
            script.crossOrigin = 'anonymous';
            script.onload = () => {
                try {
                    ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
                    setIsLoaded(true);
                } catch (err) {
                    console.error('Error loading Google Ad:', err);
                }
            };
            document.head.appendChild(script);
        }
    }, [adSlot, shouldRenderAd]);

    if (!shouldRenderAd) {
        return null;
    }

    // Default styles based on format
    const defaultStyle: React.CSSProperties = {
        display: 'block',
        textAlign: 'center',
        minHeight: format === 'rectangle' ? '250px' : format === 'horizontal' ? '90px' : '100px',
        ...style,
    };

    return (
        <Box
            ref={adRef}
            className={className}
            sx={{
                ...defaultStyle,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '16px 0',
                backgroundColor: showPlaceholder && !isLoaded ? 'action.hover' : 'transparent',
                minHeight: defaultStyle.minHeight,
            }}
        >
            {showPlaceholder && !isLoaded && (
                <Box
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        padding: 2,
                    }}
                >
                    Loading ad...
                </Box>
            )}
            <ins
                className="adsbygoogle"
                style={{
                    display: 'block',
                    ...defaultStyle,
                }}
                data-ad-client={AD_CONFIG.publisherId}
                data-ad-slot={adSlot}
                data-ad-format={format}
                data-full-width-responsive="true"
            />
        </Box>
    );
};

export default GoogleAd;
